<script setup lang="ts">
import useLanguage, { useInlineLanguage } from '@/composables/useLanguage'
import { ISticker } from '@lifk/types'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Which sticker image style should be used ? */
    stickerType?: 'with-back' | 'no-back' | 'peeling'
    /** Which sticker image asset should be displayed? */
    stickerImg: ISticker
    /** What size variant should the sticker use? */
    variant?: keyof typeof UIStickerVariants
    /** What custom size (in pixels) should the sticker be, if using the `CUSTOM` variant? */
    customSize?: number
    /** Should the sticker show its peeled image regardless of hover? */
    peeled?: boolean
    /** Should the peel animation be disabled? */
    preventPeel?: boolean
    /** Should hover effects be disabled? */
    preventHover?: boolean
    /** Should the sticker display a drop shadow? */
    hasShadow?: boolean
    /** Should the sticker use alt text? (Should be false if the sticker is part of a labeled button) */
    useAltText?: boolean
  }>(),
  {
    stickerType: 'no-back',
    variant: 'M',
    peeled: false,
    preventPeel: false,
    preventHover: false,
    hasShadow: false,
    useAltText: true
  }
)

const UIStickerVariants = {
  S: { class: 'w-[45px] h-[45px]' },
  M: { class: 'w-[55px] h-[55px] text-[2rem] text-center' },
  TRAY: { class: 'w-[50px] h-[50px] sm:w-15 sm:h-15' },
  L: { class: 'w-[55px] h-[55px] sm:w-[75px] sm:h-[75px]' },
  CUSTOM: { class: 'text-[2rem] text-center' },
  REFERENCES: {
    class: 'w-15 h-15 sm:w-20 sm:h-20 md:w-[120px] md:h-[120px]'
  }
}

const { t } = useInlineLanguage()
const i18n = useLanguage('UISticker')

const hovered = ref(false)

const peelingState = computed(() => {
  return (
    !props.preventPeel &&
    props.stickerType !== 'no-back' &&
    (props.peeled || (hovered.value && !props.preventHover))
  )
})

const altText = computed(() => {
  if (props.stickerImg) {
    return t(props.stickerImg?.altText || '')
  } else {
    return i18n.value.sticker
  }
})

const imgSource = computed(() => {
  if (peelingState.value || props.stickerType === 'peeling') {
    return props.stickerImg.peeling
  } else if (props.stickerType === 'with-back') {
    return props.stickerImg.withBack
  } else {
    return props.stickerImg.noBack
  }
})

const stickerClass = computed(() => {
  let classes = UIStickerVariants[props.variant].class
  if (props.hasShadow) {
    classes += ' drop-shadow-darker-half transform-gpu'
  }
  return classes
})

const customStyles = computed(() => {
  if (props.variant === 'CUSTOM' && props.customSize) {
    return {
      height: `${props.customSize}px`,
      width: `${props.customSize}px`
    }
  }
  return {}
})

// Note: no alt text provided for the image because it is generally
// used within a button that already has a label
</script>

<template>
  <img
    :class="stickerClass"
    :style="customStyles"
    :src="imgSource"
    :alt="props.useAltText ? altText : ''"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
  />
</template>
